import type { BenefitNotice } from '../../../lib/site';
import type { T } from '../../../lib/i18n-core';
import { Icon } from '../../Icon';
import { won } from '../price';
import { noticeKey } from './benefitEstimate';

/**
 * 진행 중 혜택 — **조건을 문장으로** 적고, 조건이 아직 안 맞으면 **채우도록 유도**한다
 * (2026-08-24 사용자 지시).
 *
 이 카드는 **읽을 것만** 담는다 — 무엇을 하면 무엇을 받나
 * (`전체에서 볼 수 있는 단가 · 2개 이상 구매 시 → 10% 할인`).
 * 함께 담을 상품을 고르는 자리는 구매 박스의 세 번째 묶음이다(`BenefitPicks`) —
 * 담을 것은 담는 자리에 모아 둔다(2026-08-24 사용자 제안).
 */
export function BenefitNotices({ items, t, priceDetail, metKeys, product, marketPoint }: {
  items: BenefitNotice[];
  t: T;
  /** 강좌 단가 세분화 — 켠 마켓은 **어느 단가로 산 건**에 걸리는지까지 적는다 */
  priceDetail: boolean;
  /**
   * **실제로 걸리는** 구성 — '적용 중' 을 켠다.
   * 개수가 맞아도 고른 단가가 혜택의 채널과 다르면 걸리지 않는다(그때는 꺼진다).
   */
  metKeys: string[];
  /**
   * 이 상품의 쿠폰·포인트 사용 — **혜택과 다를 때만** 그 차이를 적는다.
   * `point` 는 마켓 설정까지 반영한 값이다(끈 마켓은 어떤 경우에도 무효).
   */
  product: { coupon: boolean; point: boolean };
  /** 마켓이 포인트를 쓰나 — 끈 마켓에서는 혜택이 허용해도 포인트를 못 쓴다 */
  marketPoint: boolean;
}) {
  if (!items.length) return null;
  const met = new Set(metKeys);
  return (
    <section className="pd-benefit">
      <p className="pd-benefit-head">
        <Icon name="gift" /> {t('prd.benefit.title')}
      </p>
      <ul className="pd-benefit-list">
        {items.map((b) => {
          const on = met.has(noticeKey(b));
          return (
            <li key={noticeKey(b)} className={`pd-benefit-item${on ? ' is-on' : ''}`}>
              <div className="pd-benefit-when">
                {when(b, t, priceDetail)}
                {on && <span className="pd-benefit-on">{t('prd.benefit.applied')}</span>}
              </div>
              <div className="pd-benefit-what">
                {gives(b, t).map((g) => <span key={g} className="pd-benefit-give">{g}</span>)}
              </div>
              {/*
               * 적용이 꺼진 대상(`bnf_yn = false`)은 개수만 채운다 — 이 말이 없으면
               * "혜택이 있다더니 왜 안 깎이냐" 가 된다.
               */}
              {!b.applied && <p className="pd-benefit-note">{t('prd.benefit.notApplied')}</p>}
              {/*
                함께쓰기 — 엔진은 적용 시 카트 줄의 `coupon_enabled`·`point_enabled` 를
                혜택 값으로 **덮는다**. 상품과 다를 때만 적는다(같으면 말할 것이 없다).
              */}
              {useNote(b, product, marketPoint, t) && (
                <p className="pd-benefit-use">{useNote(b, product, marketPoint, t)}</p>
              )}
              {(b.startDate || b.endDate) && (
                <p className="pd-benefit-period">
                  {t('prd.benefit.period', { from: b.startDate ?? '', to: b.endDate ?? '' })}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** 조건 — 어느 단가로, 몇 개를 살 때 */
function when(b: BenefitNotice, t: T, priceDetail: boolean): string {
  const parts: string[] = [];
  /*
   * 어느 단가로 산 건에 걸리나 — **단가 세분화를 켠 마켓은 셋 중 무엇인지 적는다**
   * (2026-08-24 사용자 지시). 끈 마켓은 살 수 있는 단가가 하나뿐이라 적을 것이 없다.
   * 값이 비어 있으면(이관분 `bnfprd IS NULL`) 어느 단가로 사도 걸린다.
   * **문장 앞**에 온다 — 뒤에 붙이면 "…구매 시 · …구매 시" 로 같은 말이 두 번 나온다.
   */
  if (priceDetail && b.apply) {
    const ch = b.apply.platform;
    parts.push(ch === 'PC' || ch === 'MOBILE' || ch === 'ALL'
      ? t(`prd.benefit.channel.${ch}`)
      : t('prd.benefit.channelAny'));
  }
  if (b.countFrom && b.countTo) parts.push(t('prd.benefit.countTo', { from: b.countFrom, to: b.countTo }));
  else if (b.countFrom) parts.push(t('prd.benefit.count', { from: b.countFrom }));
  else parts.push(t('prd.benefit.any'));
  return parts.join(' · ');
}

/** 혜택 — 할인·연장·증정을 한 조각씩 */
function gives(b: BenefitNotice, t: T): string[] {
  const out: string[] = [];
  const v = b.apply?.value ?? '';
  if (b.apply && v) {
    if (b.apply.type === 'RATE_DISCOUNT') out.push(t('prd.benefit.rate', { v }));
    else if (b.apply.type === 'AMOUNT_DISCOUNT') out.push(t('prd.benefit.amount', { v: won(Number(v) || 0) }));
    // 기간연장 값은 길이 10 이면 날짜, 아니면 일수다(엔진 규격과 같은 판정)
    else if (b.apply.type === 'PERIOD_EXTEND')
      out.push(t(v.length === 10 ? 'prd.benefit.extendDate' : 'prd.benefit.extendDays', { v }));
  }
  for (const g of b.gifts.products) {
    const gv = g.value ?? '';
    if (g.isBook) out.push(t('prd.benefit.giftBook', { name: g.name, v: gv || '1' }));
    else out.push(t(gv.length === 10 ? 'prd.benefit.giftCourseDate' : 'prd.benefit.giftCourse', { name: g.name, v: gv }));
    if (g.freeShipping) out.push(t('prd.benefit.freeShipping'));
  }
  for (const c of b.gifts.coupons) out.push(t('prd.benefit.giftCoupon', { name: c.name, v: c.value || '1' }));
  if (b.gifts.point) out.push(t('prd.benefit.giftPoint', { v: won(Number(b.gifts.point) || 0) }));
  return out;
}

/**
 * 혜택이 걸리면 쿠폰·포인트가 어떻게 되나 — **상품과 다를 때만** 한 줄 적는다.
 * 엔진이 적용 시 카트 줄의 값을 혜택 값으로 덮으므로, 상품이 허용해도 혜택이 막으면 못 쓴다.
 */
function useNote(
  b: BenefitNotice,
  product: { coupon: boolean; point: boolean },
  marketPoint: boolean,
  t: T,
): string | null {
  const coupon = b.couponEnabled;
  const point = marketPoint && b.pointEnabled;
  if (coupon === product.coupon && point === product.point) return null;
  const key = coupon && point ? 'both' : coupon ? 'couponOnly' : point ? 'pointOnly' : 'none';
  return t(`prd.benefit.use.${key}`);
}
